import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { useAuth } from "../context/AuthContext";

/**
 * Guards every route below the app shell: redirects to /login when there
 * is no session (kind "anon"), and to /change-password when SFTPGo still
 * requires a password change, before any other page renders. Partners are
 * not forced to change their password at first sign-in (manager decision,
 * 2026-09-11); this redirect only fires today when staff set the flag
 * directly in WebAdmin, not as part of the normal onboarding flow (ADR
 * 0008 section 2). Shows a retry screen instead of an endless spinner if
 * the initial GET /session/me itself failed (network, proxy, 5xx: distinct
 * from "anon", which is a normal, successful "signed out" response).
 */
export function RequireAuth() {
  const { status, me, error, refresh } = useAuth();
  const location = useLocation();

  if (status === "loading" || !me) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (status === "error") {
    return (
      <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center", height: "100vh", px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          {error ?? "Could not load your session."}
        </Alert>
        <Button variant="contained" onClick={() => void refresh()}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (me.mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}
